import { useEffect, useState } from "react";
import { api } from "@/lib/apiClient";
import { GraduationCap, BookMarked, Landmark, BookOpen, ExternalLink, Library, ShieldCheck, Sprout, Monitor, Sparkles } from "lucide-react";
import { CLASS_HERO } from "@/lib/assets";
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from "@/components/ui/tooltip";

const GROUP_META = {
  "University & Diaspora Studies": { over: "Accredited Degree Programs", icon: GraduationCap, sub: "Africana, African Diaspora and Caribbean studies at established universities across the U.S., the Caribbean and Brazil — taught by leading Black, African and Caribbean scholars." },
  "Cultural Institutions": { over: "Museums & Heritage Centers", icon: Landmark, sub: "Learning programs, fellowships and free digital curricula from the great archives and museums of the Black experience." },
  "Online Courses": { over: "Learn From Anywhere", icon: Monitor, sub: "Free and accredited online courses in African, African American and Caribbean studies from world universities." },
  "Esoteric Schools": { over: "Living Spiritual Traditions", icon: Sparkles, sub: "Structured training in Kamitic, Ifá and Òrìṣà traditions from established schools with real enrollment." },
  "Other Alternative Teachings": { over: "Natural & Earth Practices", icon: Sprout, sub: "Holistic, natural and earth-centered paths — herbal medicine, energy healing, movement arts, food sovereignty and contemplative practice — with reputable, community-rooted teachers." },
};
const GROUP_ORDER = ["University & Diaspora Studies", "Cultural Institutions", "Online Courses", "Esoteric Schools", "Other Alternative Teachings"];

const SOURCE_ICON = { official: Landmark, text: BookOpen, reference: Library };
const SOURCE_LABEL = { official: "Official", text: "Primary Text", reference: "Reference" };

function fmtDate(iso) {
  if (!iso) return null;
  try { return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }); }
  catch { return iso; }
}
function latestVerified(sources = []) {
  const dates = sources.map((s) => s.verified_on).filter(Boolean).sort();
  return dates.length ? dates[dates.length - 1] : null;
}

export default function Classes() {
  const [classes, setClasses] = useState([]);
  useEffect(() => { api.get("/classes").then((r) => setClasses(r.data)); }, []);

  const grouped = {};
  classes.forEach((c) => {
    const g = c.group || "University & Diaspora Studies";
    (grouped[g] = grouped[g] || []).push(c);
  });
  const orderedGroups = [
    ...GROUP_ORDER.filter((g) => grouped[g]?.length),
    ...Object.keys(grouped).filter((g) => !GROUP_ORDER.includes(g)),
  ];

  return (
    <main className="bg-onyx min-h-screen pt-16" data-testid="classes-page">
      <div className="relative border-b border-white/10 grain bg-surface overflow-hidden">
        <img src={CLASS_HERO} alt="" className="absolute inset-0 w-full h-full object-cover opacity-30" />
        <div className="absolute inset-0 bg-gradient-to-t from-onyx via-onyx/85 to-onyx/50" />
        <div className="relative max-w-7xl mx-auto px-5 md:px-8 py-20">
          <p className="text-xs uppercase tracking-[0.3em] text-gold mb-4 flex items-center gap-2"><GraduationCap size={16} /> Classes & Training</p>
          <h1 className="font-serif text-4xl md:text-6xl text-bone mb-4">Guided Learning Paths</h1>
          <p className="text-bone/60 max-w-2xl text-lg">Structured programs and courses from established universities, cultural institutions, online platforms and spiritual schools — led by African American, African, Caribbean and Indigenous teachers of the Diaspora.</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-5 md:px-8 py-16 space-y-20">
        {orderedGroups.map((g, gi) => {
          const meta = GROUP_META[g] || { over: "Learning Path", icon: GraduationCap, sub: "" };
          const Icon = meta.icon;
          return (
            <section key={g} className={gi > 0 ? "border-t border-white/10 pt-14" : ""} data-testid={`class-group-${gi}`}>
              <div className="mb-10">
                <p className="text-xs uppercase tracking-[0.3em] text-gold mb-4 flex items-center gap-2"><Icon size={16} /> {meta.over}</p>
                <h2 className="font-serif text-3xl md:text-5xl text-bone mb-3">{g}</h2>
                {meta.sub && <p className="text-bone/60 max-w-2xl text-lg">{meta.sub}</p>}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {grouped[g].map((c) => <ClassCard key={c.id} c={c} />)}
              </div>
            </section>
          );
        })}
      </div>
    </main>
  );
}

function ClassCard({ c }) {
  return (
    <div className="bg-surface border border-white/10 p-8 md:p-10 hover:border-gold/40 transition-colors" data-testid="class-card">
      <p className="text-xs uppercase tracking-widest text-gold mb-3">{c.organization}</p>
      <h2 className="font-serif text-3xl text-bone mb-4">{c.title}</h2>
      <p className="text-bone/60 leading-relaxed mb-6">{c.description}</p>

      {c.sources?.length > 0 && (
        <div className="mb-2 border-t border-white/10 pt-6" data-testid="class-sources">
          <div className="flex items-center justify-between gap-2 mb-4 flex-wrap">
            <p className="text-xs uppercase tracking-[0.2em] text-gold flex items-center gap-2">
              <BookMarked size={14} /> Original Sources
            </p>
            {latestVerified(c.sources) && (
              <TooltipProvider delayDuration={100}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span tabIndex={0} className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-emerald-300 bg-emerald-500/10 border border-emerald-400/30 px-2.5 py-1 rounded-sm cursor-help" data-testid="verified-badge">
                      <ShieldCheck size={13} strokeWidth={2} /> Verified · {fmtDate(latestVerified(c.sources))}
                    </span>
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs bg-surface border border-emerald-400/30 text-bone" data-testid="verified-tooltip">
                    We checked the original source links for this path and confirmed they are live and reachable as of this date. "Verified" means the teachings link to authentic, working sources — not that we endorse any single interpretation.
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
          <ul className="space-y-2">
            {c.sources.map((s, i) => {
              const Icon = SOURCE_ICON[s.kind] || BookOpen;
              const broken = s.health === "broken";
              return (
                <li key={i}>
                  <a href={s.url} target="_blank" rel="noreferrer" data-testid="class-source-link"
                    className="group flex items-start gap-3 text-bone/70 hover:text-gold transition-colors">
                    <Icon size={16} className="mt-0.5 shrink-0 text-gold/70" strokeWidth={1.5} />
                    <span className="flex-1 text-sm leading-snug">
                      {s.label}
                      <span className="ml-2 text-[10px] uppercase tracking-widest text-bone/30 border border-white/10 px-1.5 py-0.5 rounded-sm">{SOURCE_LABEL[s.kind] || "Source"}</span>
                      {broken && (
                        <TooltipProvider delayDuration={100}>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span tabIndex={0} onClick={(e) => e.preventDefault()} className="ml-2 inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-amber-300 border border-amber-400/30 px-1.5 py-0.5 rounded-sm cursor-help" data-testid="source-under-review">
                                ⚠ Under Review
                              </span>
                            </TooltipTrigger>
                            <TooltipContent className="max-w-xs bg-surface border border-amber-400/30 text-bone" data-testid="under-review-tooltip">
                              Our automated link checker could not reach this source recently (it may be down, moved, or temporarily offline). We're reviewing it — the link may still work, so feel free to try it.
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      )}
                    </span>
                    <ExternalLink size={13} className="mt-0.5 opacity-0 group-hover:opacity-100 transition-opacity" />
                  </a>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}
